'use client';

import { MissionSection as MissionSectionUI } from '@wwe-portal/widget/club-store';
import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MissionOfferDialog } from '@wwe-portal/web/components/club-store/modals/OfferDialog';
import { useMutateClaimAll } from '../../providers/queries/club-store.mutation';
import {
  mapMissionTypeToLabel,
  useClaimAllStatus,
  useMissionItems,
  useMissionStatus,
} from '../../providers/queries/club-store';
import { Button } from '@wwe-portal/ui/components/button';
import { usePortableDialog } from '@wwe-portal/ui/components/dialog/portable-dialog';
import { MilestonePeriodData } from '@wwe-portal/ui/features/club-store/milestone/milestone-period';
import { OfferItem, OfferReviewDialog } from '@wwe-portal/ui/features/offer';
import { MissionItemResponse } from '@wwe-portal/entities/club-store.type';

interface SortableMission {
  isClaimedValue: number;
  hasPinValue: number;
  endPoint: number;
  periodType: number;
  currentPoint: number;
  milestoneId: string;
}

const comparedKeys = [
  'hasPinValue',
  'isClaimedValue',
  'periodType',
  'endPoint',
  'currentPoint',
] as const;
const isMissionBetter = (a: SortableMission, b: SortableMission) => {
  for (const field of comparedKeys) {
    const d = a[field] - b[field];
    if (d !== 0) return d;
  }
  return a.milestoneId.localeCompare(b.milestoneId);
};

const parseMissionItemToProps = (rawItem: MissionItemResponse) => {
  const props: Omit<MilestonePeriodData, 'isClaimed' | 'updatedAt'> = {
    id: rawItem.subEventId.toString(),
    milestoneId: rawItem.mission.milestoneId,
    periodType: rawItem.type,
    endTime: rawItem.endDate,
    currentPoint: 0,
    endPoint: rawItem.mission?.requirement,
    title: rawItem?.type
      ? (mapMissionTypeToLabel[rawItem.type] ?? '')
      : rawItem.name,
    imgUrl: rawItem.mission?.reward?.imageUrl,
    currencyType: rawItem.mission?.reward?.itemId,
    description: rawItem.description,
    quantity: rawItem.mission?.reward?.quantity,
  };
  return props;
};

export const MissionSection = () => {
  const modal = usePortableDialog(MissionOfferDialog);
  const t = useTranslations();
  const [isSectionModal, setIsSectionModal] = useState(false);
  const { missions } = useMissionItems();
  const { data: missionStatus } = useMissionStatus();
  const { data: claimAllStatus } = useClaimAllStatus();
  const isClaimAllAble = useMemo(
    () => Boolean(claimAllStatus?.data.length),
    [claimAllStatus]
  );

  const { mutate } = useMutateClaimAll();
  const [isViewMore, setIsViewMore] = useState(false);
  const isRevamp = false;
  const [groupMission, setGroupMission] = useState<OfferItem[]>([]);

  const missionItems = useMemo(
    () =>
      missions?.sort((a, b) => a.type - b.type).map(parseMissionItemToProps) ??
      [],
    [missions]
  );

  const sortableData = useMemo(
    () =>
      (missionItems as MilestonePeriodData[])
        .map((record) => {
          const currentPoint =
            (missionStatus?.[record.milestoneId]?.count ?? 0) > record.endPoint
              ? record.endPoint
              : (missionStatus?.[record.milestoneId]?.count ?? 0);
          const hasPin = record.endPoint === currentPoint;
          const isClaimable =
            claimAllStatus?.data.some(
              ({ milestoneId }) => milestoneId === record.milestoneId
            ) ?? true;
          const isClaimed = hasPin && !isClaimable;
          const updatedAt = new Date(
            missionStatus?.[record.milestoneId]?.updatedAt ?? ''
          );
          return {
            ...record,
            currentPoint,
            hasPinValue: record.endPoint > currentPoint ? -1 : 1,
            isClaimed,
            hasPin,
            isClaimedValue: !isClaimed ? -1 : 1,
            updatedAt: updatedAt.getTime(),
          };
        })
        .sort(isMissionBetter) ?? [],
    [missionItems, missionStatus, claimAllStatus]
  );

  const topMissions = useMemo(() => {
    return Object.keys(mapMissionTypeToLabel)
      .map((type) =>
        sortableData.find(
          (mission) => String(mission.periodType) === String(type)
        )
      )
      .filter(
        (item) =>
          Boolean(item) &&
          (item?.hasPinValue !== 1 || item?.isClaimedValue !== 1)
      ) as MilestonePeriodData[];
  }, [sortableData]);

  const topMissionIds = new Set(
    topMissions.map((mission) => mission?.milestoneId)
  );

  const viewMoreMissions = sortableData.filter(
    (record) => !topMissionIds.has(record.milestoneId)
  );

  const viewReward = (id: string) => {
    const rawOffer = missions?.find(
      (item) => item.subEventId.toString() === id
    );
    if (rawOffer == null) return;
    modal.show({
      data: {
        title: 'MISSION REWARDS',
        description: rawOffer.type
          ? (mapMissionTypeToLabel[rawOffer.type] ?? '')
          : rawOffer.name,
        detailsDescription: rawOffer.description,
        rewardList: rawOffer.mission.reward?.pools?.map(
          ({ displayName, quantity }) => `${quantity} ${displayName}`
        ),
        isBlocked: false,
      },
      type: rawOffer.type,
    });
  };

  return (
    <>
      <MissionSectionUI
        isRevamp={isRevamp}
        claimAllTooltipContent={t('clubStore_claimAll_description')}
        data={{
          defaultMileStonePeriods: topMissions,
          milestonePeriods: isRevamp || isViewMore ? viewMoreMissions : [],
          onMissionDetails: viewReward,
        }}
        description={t('subscription_mission_description')}
        isClaimAllAble={isClaimAllAble}
        title="Missions"
        onClaim={mutate}
        onMissionSectionClick={(type) => {
          const g = sortableData
            ?.filter((i) => i.periodType === type)
            .map((i) => {
              const { id, imgUrl, quantity, currentPoint, endPoint } = i;
              return {
                itemId: id,
                defaultImageUrl: imgUrl,
                dynamicImageUrl: imgUrl,
                percentage: Math.ceil((currentPoint / endPoint) * 100),
                text: `${quantity}`,
              };
            });

          setGroupMission(g ?? []);
          setIsSectionModal(true);
        }}
        viewMoreBtn={
          topMissions.length < missionItems.length && (
            <div className="flex w-full justify-center pt-5">
              <Button
                className="h-[44px] w-full font-semibold md:w-[200px]"
                onClick={() => {
                  setIsViewMore(!isViewMore);
                }}
                variant="secondary"
              >
                View {isViewMore ? 'Less' : 'More'}
              </Button>
            </div>
          )
        }
      />
      <OfferReviewDialog
        onClose={() => setIsSectionModal(false)}
        open={isSectionModal}
        isAggregate
        onItemClick={(id) => {
          viewReward(id);
        }}
        data={{
          title: '',
          items: groupMission,
          vipPoints: 100,
          endDate: '',
          id: '1',
          actionBtn: null,
          storeItemDescription: '',
        }}
      />
    </>
  );
};
